import { PencilIcon } from 'assets/icons';
import React, { ChangeEvent, useMemo, useState } from 'react';
import { SidePanelProfileClinicPetParentFragment, useUpdateOneClinicPetParentMutation } from 'shared/types/graphql';
import styled from 'styled-components/macro';
import { email as emailAddressRegex } from 'shared/utils/validation';
import { Spinner } from '@televet/kibble-ui';
interface IClientEmailPopoverProps {
  clinicPetParent: SidePanelProfileClinicPetParentFragment;
  goToPetParent: (id: string) => void;
}

const ClientEmailPopover = ({ clinicPetParent, goToPetParent }: IClientEmailPopoverProps): JSX.Element => {
  const { email } = clinicPetParent;

  const [draftEmail, setDraftEmail] = useState(email || '');
  const [isEditing, setIsEditing] = useState(false);

  const emailValidationMessage = useMemo(() => {
    if (!emailAddressRegex.test(draftEmail)) {
      return 'Please enter a valid email address';
    } else return '';
  }, [draftEmail]);

  const [updateClinicPetParent, { loading }] = useUpdateOneClinicPetParentMutation();

  const onDraftEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newEmail = event.target.value;

    setDraftEmail(newEmail || email || '');
  };

  const onDraftEmailSave = async (): Promise<void> => {
    if (!emailValidationMessage && email != draftEmail) {
      await updateClinicPetParent({ variables: { where: { id: clinicPetParent.id }, data: { email: draftEmail } } });
      goToPetParent(clinicPetParent.id);
    }
  };

  const onDraftEmailCancel = (): void => {
    setIsEditing(false);
    setDraftEmail(email || '');
  };

  return (
    <Container>
      {!loading ? (
        <>
          <Header>Client Email</Header>

          <EmailListItem
            onClick={(): void => {
              setIsEditing(true);
            }}
          >
            <Email>
              <div>{email}</div>
            </Email>
            <StyledPencilIcon />
          </EmailListItem>

          <HR />
          {isEditing && (
            <Footer showBackground={!!draftEmail}>
              <DraftEditContainer>
                <>
                  <DraftEditHeader>{email ? 'Edit' : 'Add'} email</DraftEditHeader>
                  <EmailInput
                    type="text"
                    autoComplete="off"
                    value={draftEmail}
                    onChange={onDraftEmailChange}
                    onKeyDown={(e): void => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        onDraftEmailSave();
                      } else if (e.key === 'Escape') {
                        onDraftEmailCancel();
                      }
                    }}
                  />
                  {emailValidationMessage && <ValidationMessage>{emailValidationMessage}</ValidationMessage>}
                </>

                <DraftButtonsContainer>
                  <DraftCancelButton onClick={onDraftEmailCancel}>Cancel</DraftCancelButton>
                  <DraftSaveButton
                    onClick={onDraftEmailSave}
                    isDisabled={!!emailValidationMessage || email === draftEmail}
                  >
                    Save
                  </DraftSaveButton>
                </DraftButtonsContainer>
              </DraftEditContainer>
            </Footer>
          )}
        </>
      ) : (
        <Spinner />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  color: #757c8d;
  min-height: 80px;
  justify-content: center;
`;

const HR = styled.hr`
  border: none;
  border-top: 1px solid #ddd;
  margin-top: 4px;
`;

const Header = styled.div`
  font-weight: 600;
  padding: 8px 14px;
  color: #6b7081;
`;

const ListItem = styled.div`
  cursor: pointer;
  padding: 8px 14px;

  &:hover {
    background: #e6f4f6;
  }
`;

const StyledPencilIcon = styled(PencilIcon)`
  opacity: 0;
  flex: 0 0 auto;
`;

const EmailListItem = styled(ListItem)`
  display: flex;
  align-items: center;

  &:hover {
    ${StyledPencilIcon} {
      opacity: 1;
    }
  }
`;

const Email = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
`;

interface IFooterProps {
  showBackground: boolean;
}

const Footer = styled.div<IFooterProps>`
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  background: ${({ showBackground }: IFooterProps): string => (showBackground ? '#f8f8f8' : '#fff')};
`;

const DraftEditContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const DraftEditHeader = styled.div`
  margin: 4px 14px 4px;
  color: #6b7081;
  font-weight: 600;
`;

const EmailInput = styled.input`
  font-size: 14px;
  color: #6b7081;
  padding: 6px 8px;
  border-radius: 5px;
  border: 1px solid #d2d2d2;
  background-color: var(--chakra-colors-background-default);
  margin: 4px 6px 0;

  &:focus {
    border-color: #52b0c3;
  }
`;

const ValidationMessage = styled.div`
  margin: 8px 14px;
  line-height: 1.4;
  font-size: 12px;
  color: var(--chakra-colors-text-danger);
  max-width: 180px;
`;

const DraftButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 8px 6px 4px;
`;

const DraftCancelButton = styled.button`
  border-radius: 8px;
  flex: 1 1 auto;
  width: 50%;
  padding: 6px 0;
  font-weight: 600;
  cursor: pointer;

  background: transparent;
  border: 1px solid #ccc;
  margin-right: 3px;
`;

const DraftSaveButton = styled.button<{ isDisabled: boolean }>`
  background: ${({ isDisabled }): string => (isDisabled ? '#e3e8e8' : '#52b0c3')};
  border: 1px solid ${({ isDisabled }): string => (isDisabled ? '#e3e8e8' : '#52b0c3')};
  color: #fff;
  border-radius: 8px;
  flex: 1 1 auto;
  width: 50%;
  padding: 6px 0;
  font-weight: 600;
  cursor: pointer;
  margin-left: 3px;
`;

export default ClientEmailPopover;
